import { useMutation } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApproveApi } from 'apis/ApproveApis';
import { IUpdateApprovalStatus } from 'apis/GraphQL/Variables/approve';
import { ButtonFilter } from 'components';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import useQueryParam from 'hooks/useQueryPrams';
import { IPageInfo } from 'interfaces/Approval';
import {
  IUserApproval,
  IUserApprovalData,
  TGetUserApproval,
} from 'interfaces/Approve/Approve';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import { toastError, toastSuccess } from 'utils/constants/functions';
import FilterApprovePage from './FilterApprovePage';
import TableApprovePage from './TableApprovePage';

export enum APPROVAL_STATUS {
  PENDING_APPROVAL = 'AP100_01',
  APPROVED = 'AP100_02',
}

const initData: IUserApprovalData = {
  items: [],
  pageInfo: {} as IPageInfo,
};

const ApproveList = () => {
  const navigate = useNavigate();

  const { user } = useUser();
  const { setTitle, setBreadcrumb } = useTitle();

  const [formParams, setFormParams] = useState<any>({});
  const [isOpenFilter, setIsOpenFilter] = useState(false);

  const [data, setData] = useState<IUserApprovalData>(initData);

  const queryParam = useQueryParam(),
    page = parseInt(queryParam.get('page') + '') || 1,
    page_size = parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

  const { isPending, mutate: getAllUserApproval } = useMutation({
    mutationFn: (params: TGetUserApproval) =>
      ApproveApi.getUserApproval(params),
    onSuccess: (res) => {
      const items = res.data?.getUserApproval?.items;
      const pageInfo = res.data?.getUserApproval?.pageInfo;

      setData({ items, pageInfo });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutate: updateApprovalStatus } = useMutation({
    mutationFn: (params: IUpdateApprovalStatus) =>
      ApproveApi.updateApprovalStatus(params),
    onSuccess: (res: any) => {
      if (res.errors?.length) {
        toastError(res.errors?.[0]?.message || 'Có lỗi xảy ra');
        return;
      }

      toastSuccess('Thành công');
      refetch();
    },
    onError: (error) => {
      console.log(error);
      toastError(`Phê duyệt thất bại: ${error.message}`);
    },
  });

  const refetch = useCallback(() => {
    const params: TGetUserApproval = {
      page,
      pageSize: page_size,
      ...(user?.isAdmin || user?.isHO
        ? {}
        : {
            tuserApprovalDetail: user?.email,
          }),
      ...formParams,
    };

    getAllUserApproval(params);
  }, [page, page_size, formParams, getAllUserApproval]);

  useEffect(() => {
    setTitle('Phê duyệt');
    setBreadcrumb([{ title: 'Phê duyệt' }]);
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const handleCancelFilter = useCallback(() => {
    setFormParams({});
  }, []);

  const handleFinishFilter = useCallback((values: any) => {
    navigate(`?page=1&page_size=${page_size}`);

    setFormParams(values);
  }, []);

  const handleTableApprove = useCallback(
    (data: IUserApproval) => {
      if (!data.tuserApprovalDetail.length) {
        toastError('Không tim thấy thông tin phê duyệt!');
        return;
      }

      const userApprovalDetail =
        user?.isAdmin || user?.isHO
          ? data.tuserApprovalDetail?.filter(
              (item) => item?.email === user?.email,
            )
          : data.tuserApprovalDetail;

      updateApprovalStatus({
        id: userApprovalDetail?.[0]?.id,
        approvalYn: true,
      });
    },
    [user],
  );

  const handleTableCorrectionRequest = useCallback((data: IUserApproval) => {
    if (!data.tuserApprovalDetail.length) {
      toastError('Không tim thấy thông tin phê duyệt!');
      return;
    }

    const userApprovalDetail =
      user?.isAdmin || user?.isHO
        ? data.tuserApprovalDetail?.filter(
            (item) => item?.email === user?.email,
          )
        : data.tuserApprovalDetail;

    updateApprovalStatus({
      id: userApprovalDetail?.[0]?.id,
      approvalYn: false,
    });
  }, []);

  return (
    <>
      <Row gutter={[8, 16]} align={'middle'} justify={'end'}>
        <Col>
          <ButtonFilter isOpen={isOpenFilter} setIsOpen={setIsOpenFilter} />
        </Col>
      </Row>
      <FilterApprovePage
        isOpen={isOpenFilter}
        onCancel={handleCancelFilter}
        onFinish={handleFinishFilter}
      />
      <TableApprovePage
        data={data}
        loading={isPending}
        onApprove={handleTableApprove}
        onCorrectionRequest={handleTableCorrectionRequest}
      />
    </>
  );
};

export default ApproveList;
